import { useEffect, type ReactNode } from 'react';
import { createFileRoute, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { isApiError } from '@/api/errors';
import { meQuery, OrgProvider, useOrgContext } from '@/app/session';
import { AppShell } from '@/components/shell/AppShell';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui';

export const Route = createFileRoute('/app')({
  component: AppLayout,
  staticData: { title: 'Eryce' },
});

function AppLayout() {
  return (
    <OrgProvider>
      <SessionGate>
        <Outlet />
      </SessionGate>
    </OrgProvider>
  );
}

/**
 * Resolves the session and tenant before any private page renders.
 * 401 goes to /login with a safe return path; every other failure is shown as is.
 */
function SessionGate({ children }: { children: ReactNode }) {
  const me = useQuery(meQuery);
  const { org } = useOrgContext();
  const navigate = useNavigate();
  const href = useRouterState({ select: (s) => s.location.href });
  const unauthenticated = isApiError(me.error) && me.error.unauthenticated;

  useEffect(() => {
    if (unauthenticated) void navigate({ to: '/login', search: { redirect: href }, replace: true });
  }, [unauthenticated, navigate, href]);

  if (me.isPending || unauthenticated) {
    return (
      <main className="auth-wrap">
        <div className="auth-form">
          <LoadingState label="Checking your session" />
        </div>
      </main>
    );
  }
  let blocker: ReactNode = null;
  if (me.isError) {
    blocker = (
      <section className="page page-narrow" aria-labelledby="session-title">
        <h1 id="session-title" tabIndex={-1} data-page-heading>
          Session unavailable
        </h1>
        <ErrorState error={me.error} onRetry={() => void me.refetch()} />
      </section>
    );
  } else if (!org) {
    blocker = (
      <section className="page page-narrow">
        <h1 tabIndex={-1} data-page-heading>
          No organization
        </h1>
        <EmptyState title="Your account is not a member of any organization yet. Ask an admin for an invitation." />
      </section>
    );
  }
  // Keyed by tenant so nothing from a previous organization survives a switch.
  return <AppShell>{blocker ?? <div key={org?.orgId}>{children}</div>}</AppShell>;
}
